import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Calendar,
  Loader2,
  MessageSquareWarning,
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  X,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const AMC_WHATSAPP = "5511956649968";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : ""));
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

// Mini thumbnail da foto do serviço (view-only pra escola)
function SchoolEntryThumb({ path, tag }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let obj;
    if (path) {
      api.get(`/files/${path}`, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => setSrc(null));
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!src) return null;
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="relative w-16 h-16 rounded-md overflow-hidden border border-slate-200 shrink-0 hover:ring-2 hover:ring-indigo-400"
    >
      <img src={src} alt={tag} className="w-full h-full object-cover" />
      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center py-0.5 font-semibold uppercase tracking-wide">{tag}</span>
    </a>
  );
}

/**
 * School-side timeline of FDP entries for a budget.
 * Shows each service with status, allows contesting via in-app or WhatsApp.
 */
export default function FdpEntriesSchoolView({ budget, schoolName = "" }) {
  const [entries, setEntries] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [contestEntry, setContestEntry] = useState(null); // in-app contest modal
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!budget?.id) return;
    setLoading(true);
    try {
      const r = await api.get("/fdp-entries", { params: { budget_id: budget.id } });
      setEntries(r.data?.items || []);
      setTotalActive(r.data?.total_active || 0);
    } catch {
      toast.error("Falha ao carregar lançamentos");
    } finally {
      setLoading(false);
    }
  }, [budget?.id]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  if (!budget?.faz_depois_paga || !budget?.fdp_log_mode) return null;

  const sendContest = async () => {
    if (!contestEntry) return;
    const r = (reason || "").trim();
    if (!r) { toast.error("Explique brevemente o motivo"); return; }
    setSending(true);
    try {
      await api.patch(`/fdp-entries/${contestEntry.id}`, { status: "contested", contest_reason: r });
      toast.success("Contestação enviada — a AMC foi avisada");
      setContestEntry(null);
      setReason("");
      fetchEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const whatsappContest = (entry) => {
    const money = `R$ ${Number(entry.value || 0).toFixed(2).replace(".", ",")}`;
    const msg = encodeURIComponent(
      `Olá! Sou da ${schoolName || "nossa escola"}. Gostaria de conversar sobre o lançamento registrado no sistema FDP:\n\n"${entry.description}" — ${money} (${formatDate(entry.service_date)})\n\nQual seria a melhor hora para falarmos?`
    );
    window.open(`https://wa.me/${AMC_WHATSAPP}?text=${msg}`, "_blank");
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5" data-testid="fdp-entries-school-view">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <h3 className="font-heading text-base font-bold text-[#1e3a8a]">Serviços registrados pela AMC</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        A AMC registra aqui cada serviço prestado durante o período. Caso discorde de algum valor ou serviço, você pode contestar — sem bloquear o atendimento.
      </p>

      {/* Total */}
      <div className="bg-gradient-to-br from-indigo-50 to-sky-50 border border-indigo-200 rounded-xl p-3 mb-3 flex items-end justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] text-indigo-700 font-bold uppercase tracking-wider">Total consumido</p>
          <p className="text-xl font-black text-indigo-900 tabular-nums">{BRL(totalActive)}</p>
        </div>
        <p className="text-[11px] text-slate-500">{entries.length} serviço{entries.length !== 1 ? "s" : ""}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded-xl">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-1.5 text-emerald-500" />
          Nenhum serviço registrado ainda.
        </div>
      ) : (
        <div className="space-y-2" data-testid="school-entries-list">
          {entries.map((e) => (
            <div
              key={e.id}
              className={`rounded-xl border p-3 ${e.status === "contested" ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}
              data-testid={`school-entry-${e.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{e.description}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-0.5">
                      <Calendar className="w-3 h-3" /> {formatDate(e.service_date)}
                    </span>
                    {e.status === "contested" && (
                      <span className="text-amber-700 font-semibold inline-flex items-center gap-0.5">
                        <MessageSquareWarning className="w-3 h-3" /> contestado por você
                      </span>
                    )}
                    {e.note && <span className="italic">· {e.note}</span>}
                  </div>
                  {e.status === "contested" && e.contest_reason && (
                    <div className="mt-2 bg-white border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900">
                      <b>Seu motivo:</b> {e.contest_reason}
                    </div>
                  )}
                  {(e.before_photo_path || e.after_photo_path) && (
                    <div className="flex gap-2 mt-2">
                      {e.before_photo_path && <SchoolEntryThumb path={e.before_photo_path} tag="Antes" />}
                      {e.after_photo_path && <SchoolEntryThumb path={e.after_photo_path} tag="Depois" />}
                    </div>
                  )}
                </div>
                <p className="shrink-0 text-base font-black text-indigo-700 tabular-nums whitespace-nowrap">
                  {BRL(e.value)}
                </p>
              </div>
              {e.status !== "contested" && (
                <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setContestEntry(e); setReason(""); }}
                    className="h-7 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid={`btn-contest-${e.id}`}
                  >
                    <MessageSquareWarning className="w-3 h-3 mr-1" /> Contestar pelo app
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => whatsappContest(e)}
                    className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`btn-contest-wa-${e.id}`}
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Falar no WhatsApp
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Contest modal */}
      <Dialog open={!!contestEntry} onOpenChange={(o) => !o && setContestEntry(null)}>
        <DialogContent data-testid="school-contest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <MessageSquareWarning className="w-5 h-5" /> Contestar lançamento
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              A AMC será avisada e entrará em contato. Isso não cancela o serviço, apenas abre uma conversa sobre o valor.
            </p>
          </DialogHeader>
          {contestEntry && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
              <p className="font-semibold">{contestEntry.description}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {formatDate(contestEntry.service_date)} · <span className="font-bold text-indigo-700">{BRL(contestEntry.value)}</span>
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Por que você discorda? *</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Ex.: O serviço foi mais simples do que o esperado / combinamos um valor diferente / não reconheço este serviço..."
              data-testid="input-contest-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContestEntry(null)}>Cancelar</Button>
            <Button
              onClick={sendContest}
              disabled={sending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-send-contest"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enviando...</> : <><MessageSquareWarning className="w-4 h-4 mr-1.5" /> Enviar contestação</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
